/**
 * PolicyTermField — the control AC-2 and AC-3 depend on.
 *
 * The grouped form must contain a control that WRITES the chosen term, not only
 * one that displays it. Without it nothing sets `policy_term_key`, so
 * `validateEffectiveDateAgainstPolicy` has no chosen term to validate against
 * (AC-3 unreachable), AC-2's payload assertion cannot be driven by "select the
 * renewal term", and Phase 1's move of `policyPrefillOptions.ts` would have
 * relocated a selection helper nothing re-consumes.
 *
 * **The term-identity write is the whole of AC-2 and it is NEW work.** Today the
 * term DATES are not form state at all: the drawer derives them from `chosenTerm`
 * and injects them into RUN-LEVEL inputs. Under the reshaped contract the quartet
 * is PER ACTION, so nothing run-level can supply it — this control is the only
 * writer, and the drawer's run-level injection is superseded in the reshaped
 * branch (Phase 5 task 2 records the hand-over).
 *
 * A declared term-identity key with no `TERM_IDENTITY_SOURCES` entry, or whose
 * source is absent on the selected row, is a LOUD failure in the PAD-2 sense —
 * `missingTermIdentitySources` — surfaced by `LaunchForm` exactly like
 * `missingRoles` and blocking submit. Writing a partial identity silently is the
 * original defect (a bare policy number letting a future-term change land on the
 * current term, instances 3b35db7f / e036c04d, 2026-08-05); an empty string for a
 * declared term key is that same defect wearing a key.
 */

import type { ReactNode } from "react";
import type { FieldSchema } from "~/common/workflows/schemaHelpers";
import type { PolicyInForce } from "~/common/workflows/types/client";
// The named export, not the default: the module exports BOTH under this name,
// and importing the default under it trips `import/no-named-as-default`.
import { PolicyNumberCombobox } from "~/common/workflows/launch/PolicyNumberCombobox";
import {
  POLICY_TERM_KEY_FIELD,
  policyTermKey,
  readPolicyTermKey,
} from "~/common/workflows/launch/policyPrefillOptions";
import {
  interestAutoPickPatch,
  interestResetPatch,
} from "~/common/workflows/launch/interestTargetOptions";
import {
  LAUNCH_ROLE_CANDIDATES,
  type LaunchBinding,
} from "~/common/workflows/launch/launchSchemaBinding";

/**
 * How each DECLARED term-identity key is resolved from the selected option row.
 *
 * One documented map, in one module — the alternative is four resolution rules
 * scattered across handlers, which is how three of the four quietly stop being
 * written.
 *
 * ⚠️ `policy_type_code` is an ASSUMPTION (recorded as Q8): the `PolicyInForce`
 * row carries no `ams_*` / `*_type_code` field, so the product group is the
 * closest declared source. If the contract means Epic's own code, this mapping
 * is what has to change — and it fails LOUDLY rather than writing "" because
 * `missingTermIdentitySources` treats a blank source as missing.
 */
export const TERM_IDENTITY_SOURCES: Record<
  string,
  (opt: PolicyInForce) => unknown
> = {
  policy_number: (o) => o.policy_number,
  policy_type_code: (o) => o.product_group ?? o.policy_type,
  term_effective_date: (o) => o.effective_date,
  term_expiration_date: (o) => o.expiration_date,
};

function isBlank(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    (typeof value === "string" && value.trim() === "")
  );
}

export interface TermIdentityPlacement {
  /**
   * Where the declared term identity lives — and therefore where the ONE term
   * control renders. `null` means the schema declares no term-identity key at
   * all, which `buildLaunchBinding` has already reported as a missing role.
   */
  level: "action" | "runLevel" | null;
  /** The declared term-identity keys at that level, in candidate order. */
  keys: string[];
}

/**
 * Resolve where the term control renders — from the DECLARED schema, never from
 * a hardcoded assumption about where the contract puts the quartet.
 *
 * Action level wins when the contract declares the identity there (the reshaped
 * contract does). The run-level fallback exists so a contract that keeps the
 * quartet on the envelope still gets a writer rather than a read-only header,
 * which would make AC-2 and AC-3 unreachable.
 */
export function resolveTermIdentityPlacement(
  binding: LaunchBinding,
): TermIdentityPlacement {
  if (binding.roles.termIdentity.length > 0) {
    return { level: "action", keys: binding.roles.termIdentity };
  }
  const runLevel = LAUNCH_ROLE_CANDIDATES.termIdentity.filter((k) =>
    binding.declaredKeys.runLevel.has(k),
  );
  return runLevel.length > 0
    ? { level: "runLevel", keys: runLevel }
    : { level: null, keys: [] };
}

/**
 * Declared term-identity keys this control cannot honestly write.
 *
 * Two cases, both loud: a key with no `TERM_IDENTITY_SOURCES` entry (the
 * contract declared something this map does not know), and — once a row is
 * selected — a key whose source is blank on that row.
 */
export function missingTermIdentitySources(
  keys: readonly string[],
  option: PolicyInForce | null,
): string[] {
  const missing: string[] = [];
  for (const key of keys) {
    const source = TERM_IDENTITY_SOURCES[key];
    if (!source) {
      missing.push(key);
      continue;
    }
    if (option && isBlank(source(option))) missing.push(key);
  }
  return missing;
}

/** The FULL declared term identity, resolved from the selected row. */
export function termIdentityWrites(
  keys: readonly string[],
  option: PolicyInForce,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const key of keys) {
    const source = TERM_IDENTITY_SOURCES[key];
    if (source) patch[key] = source(option);
  }
  return patch;
}

/**
 * Every declared term-identity key back to `""` — not the policy number alone.
 * A cleared policy with a stale `term_effective_date` still on the action is a
 * term identity nobody chose.
 */
export function termIdentityClears(
  keys: readonly string[],
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const key of keys) patch[key] = "";
  return patch;
}

export interface PolicyTermFieldArgs {
  binding: LaunchBinding;
  /**
   * `mergeLaunchSlice(binding, runLevelValues, slice)` — reads go through the
   * merge because `interestResetPatch` / `interestAutoPickPatch` /
   * `readInterestOptionsForSelection` all read `policy_options`,
   * `interest_options_by_line`, `policy_number` and `policy_term_key` off ONE
   * record, and after normalisation those halves live at different levels.
   * Passing the slice alone leaves the prefill echoes behind: auto-pick and
   * `OldMortgageeSelect` then see no options.
   */
  helperValues: Record<string, unknown>;
  policyOptions: readonly PolicyInForce[];
  /** Mixed-patch writer — `LaunchForm` routes each key to its declared slice. */
  writePatch: (patch: Record<string, unknown>) => void;
  submitting?: boolean;
}

/**
 * Build the term control's `onSelect` patch.
 *
 * Order matters and is carried over verbatim from today's `policy_number`
 * override:
 *  1. `interestResetPatch` — a target resolved against the PREVIOUS term cannot
 *     describe this one; carrying it across submits another term's mortgagee row
 *     with no error anywhere.
 *  2. the FULL declared term identity from the selected row — not the policy
 *     number alone.
 *  3. `policy_term_key` — WHICH term was picked. The number alone cannot say,
 *     when a current term and its future renewal share it.
 *  4. the term-DERIVED fields (`policy_type` / `carrier_name` / `carrier_id`),
 *     each written only when the row carries it. They render locked.
 *  5. `interestAutoPickPatch` against the switched-TO term, so a term with
 *     exactly one interest pre-selects it — the same rule the server applies to
 *     the term IT resolved.
 */
export function buildTermSelectPatch(
  keys: readonly string[],
  helperValues: Record<string, unknown>,
  option: PolicyInForce,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {
    ...interestResetPatch(helperValues),
    ...termIdentityWrites(keys, option),
    [POLICY_TERM_KEY_FIELD]: policyTermKey(option),
  };
  const group = option.product_group ?? option.policy_type;
  if (group) patch.policy_type = group;
  if (option.carrier_name) patch.carrier_name = option.carrier_name;
  if (option.carrier_id) patch.carrier_id = option.carrier_id;
  // Re-merged, never the pre-switch values and never the slice alone.
  return { ...patch, ...interestAutoPickPatch({ ...helperValues, ...patch }) };
}

/** The `onClear` patch — no policy means no term, so no per-term target either. */
export function buildTermClearPatch(
  keys: readonly string[],
  helperValues: Record<string, unknown>,
): Record<string, unknown> {
  return {
    ...interestResetPatch(helperValues),
    ...termIdentityClears(keys),
    [POLICY_TERM_KEY_FIELD]: "",
  };
}

export interface PolicyTermFieldProps extends PolicyTermFieldArgs {
  /** The declared policy-number field, for label / required. */
  field: FieldSchema;
  readOnly?: boolean;
}

export default function PolicyTermField({
  binding,
  helperValues,
  policyOptions,
  writePatch,
  submitting = false,
  field,
  readOnly = false,
}: PolicyTermFieldProps) {
  const { keys } = resolveTermIdentityPlacement(binding);
  const valueKey = keys[0] ?? "policy_number";
  const value =
    typeof helperValues[valueKey] === "string"
      ? (helperValues[valueKey] as string)
      : "";

  return (
    <PolicyNumberCombobox
      label={field.label}
      required={field.required}
      value={value}
      termKey={readPolicyTermKey(helperValues)}
      options={policyOptions}
      disabled={readOnly || submitting}
      // The switch performs NO server re-trigger: the selected row carries
      // everything the pick drives, and re-running extraction would clobber the
      // AM's manual edits.
      onSelect={(opt) =>
        writePatch(buildTermSelectPatch(keys, helperValues, opt))
      }
      onClear={() => writePatch(buildTermClearPatch(keys, helperValues))}
    />
  );
}

/**
 * The same control as a `JsonSchemaForm` `fieldOverride`, for the case where the
 * declared term identity lives on the ACTION and the control therefore renders
 * inside the action card.
 */
export function policyTermFieldOverride(
  args: PolicyTermFieldArgs,
): (renderArgs: {
  field: FieldSchema;
  value: unknown;
  setValue: (key: string, next: unknown) => void;
  readOnly: boolean;
}) => ReactNode {
  // A `JsonSchemaForm` field-override is a RENDER CALLBACK, not a component —
  // it has no lifecycle of its own and the renderer never mounts it as one — so
  // the two component-shaped rules do not apply. TypeScript types the argument.
  // eslint-disable-next-line react/display-name, react/prop-types
  return ({ field, readOnly }) => (
    <PolicyTermField {...args} field={field} readOnly={readOnly} />
  );
}
